/**
 * AssetUsageDialog Component
 * Shows where an asset is used in Content Hub
 * Features:
 * - List of Content Hub items using this asset
 * - Links to view/edit each item
 * - Usage count summary
 */

import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Image as ImageIcon,
  Presentation,
  ExternalLink,
  Edit,
  Folder,
  Inbox,
  Calendar,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { contentHubService } from '@/lib/api/services/contentHub'
import type { ContentItem } from '@/lib/api/types'

// ============================================
// Types
// ============================================

export interface AssetUsageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  assetId: string
  assetName: string
  assetType: string
}

// ============================================
// AssetUsageDialog Component
// ============================================

export function AssetUsageDialog({
  open,
  onOpenChange,
  assetId,
  assetName,
  assetType,
}: AssetUsageDialogProps) {
  const navigate = useNavigate()

  // Fetch content items that use this asset
  // This queries for items where imageContent.assetId or slideContent.assetId matches
  const { data: usageItems, isLoading } = useQuery({
    queryKey: ['asset-usage', assetId],
    queryFn: async () => {
      // Search for content items that reference this asset
      // For images, check ImageElement type with this assetId
      // For presentations, check SlideElement type with this assetId
      const types = assetType === 'Presentation' ? ['SlideElement'] : ['Image']

      try {
        const result = await contentHubService.search({
          query: assetId,
          types: types as any,
          pageSize: 50,
        })

        // Filter to only items that actually reference this asset
        return result.items.filter((item: ContentItem) => {
          if (item.type === 'Image' && item.imageContent?.assetId === assetId) {
            return true
          }
          if (item.type === 'SlideElement' && item.slideContent?.assetId === assetId) {
            return true
          }
          return false
        })
      } catch {
        // If API fails, return empty array
        return []
      }
    },
    enabled: open && !!assetId,
  })

  // Count by type
  const typeCounts = useMemo(() => {
    if (!usageItems) return { images: 0, slides: 0 }
    return {
      images: usageItems.filter((item) => item.type === 'Image').length,
      slides: usageItems.filter((item) => item.type === 'SlideElement').length,
    }
  }, [usageItems])

  // Handle navigation to content item
  const handleViewItem = (item: ContentItem) => {
    onOpenChange(false)
    navigate(`/content-hub/item/${item.id}`)
  }

  const handleEditItem = (item: ContentItem) => {
    onOpenChange(false)
    navigate(`/content-hub/item/${item.id}/edit`)
  }

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-blue-500" />
            Content Hub Usage
          </DialogTitle>
          <DialogDescription>
            Showing where "{assetName}" is used in Content Hub
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Summary */}
          {!isLoading && usageItems && usageItems.length > 0 && (
            <div className="flex items-center gap-4 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                  {typeCounts.images} Image Element{typeCounts.images !== 1 ? 's' : ''}
                </span>
              </div>
              {typeCounts.slides > 0 && (
                <div className="flex items-center gap-2">
                  <Presentation className="h-4 w-4 text-orange-500" />
                  <span className="text-sm font-medium text-orange-700 dark:text-orange-300">
                    {typeCounts.slides} Slide Element{typeCounts.slides !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-neutral-200 dark:border-neutral-700">
                  <Skeleton className="h-10 w-10 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Usage List */}
          {!isLoading && usageItems && usageItems.length > 0 && (
            <ScrollArea className="max-h-[400px]">
              <div className="space-y-2">
                {usageItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 p-3 rounded-xl border border-neutral-200 dark:border-neutral-700 hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                  >
                    {/* Type Icon */}
                    <div
                      className={cn(
                        'p-2 rounded-lg',
                        item.type === 'Image'
                          ? 'bg-purple-100 dark:bg-purple-900/30'
                          : 'bg-orange-100 dark:bg-orange-900/30'
                      )}
                    >
                      {item.type === 'Image' ? (
                        <ImageIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      ) : (
                        <Presentation className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-neutral-900 dark:text-white truncate">
                        {item.name}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                        {item.folderName && (
                          <span className="flex items-center gap-1">
                            <Folder className="h-3 w-3" />
                            {item.folderName}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(item.updatedAt)}
                        </span>
                      </div>
                    </div>

                    {/* Tags */}
                    {item.tags && item.tags.length > 0 && (
                      <div className="hidden sm:flex items-center gap-1">
                        {item.tags.slice(0, 2).map((tag) => (
                          <Badge key={tag} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                        {item.tags.length > 2 && (
                          <span className="text-xs text-neutral-400">
                            +{item.tags.length - 2}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleViewItem(item)}
                        className="h-8 w-8"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditItem(item)}
                        className="h-8 w-8"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}

          {/* Empty State */}
          {!isLoading && (!usageItems || usageItems.length === 0) && (
            <div className="py-12 text-center">
              <div className="inline-flex p-3 rounded-full bg-neutral-100 dark:bg-neutral-800 mb-4">
                <Inbox className="h-8 w-8 text-neutral-400" />
              </div>
              <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
                Not used yet
              </h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 max-w-sm mx-auto">
                This asset is not currently used in any Content Hub items.
                Create an Image Element to start using it.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AssetUsageDialog
